import React, { useState, useEffect, useMemo } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth, ROLE_ADMIN } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

interface Position {
    id?: string;
    name: string;
}

export default function Positions() {
    const { user, userHasRole } = useAuth();
    const [positions, setPositions] = useState<Position[]>([]);
    const [currentPosition, setCurrentPosition] = useState<Omit<Position, 'id'>>({ name: '' });
    const [editingPosition, setEditingPosition] = useState<Position | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [loadingData, setLoadingData] = useState(true);

    const canViewAdminPage = useMemo(() => {
        if (!user) return false;
        return userHasRole(ROLE_ADMIN);
    }, [user, userHasRole]);

    const fetchPositions = React.useCallback(async () => {
        setLoadingData(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'positions'));
            const positionsList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Position[];
            setPositions(positionsList);
        } catch (err) {
            console.error('Erro ao buscar cargos:', err);
            setError('Erro ao buscar cargos');
        } finally {
            setLoadingData(false);
        }
    }, []);

    useEffect(() => {
        if (canViewAdminPage) {
            fetchPositions();
        } else {
            setLoadingData(false);
        }
    }, [canViewAdminPage, fetchPositions]);

    if (!user) {
        return <div className="text-gray-500">Carregando usuário...</div>;
    }

    if (!canViewAdminPage) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold text-gray-700 mb-4">Acesso Negado</h1>
                    <p className="text-gray-500">Você não tem permissão para visualizar esta página.</p>
                    <Link to="/" className="mt-6 inline-block bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700">
                        Voltar para o Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (loadingData) {
        return <div className="text-gray-500">Carregando cargos...</div>;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setSuccess(null);

        if (!currentPosition.name.trim()) {
            setError('O nome do cargo não pode estar vazio.');
            setIsSubmitting(false);
            return;
        }

        try {
            if (editingPosition) {
                await updateDoc(doc(db, 'positions', editingPosition.id!), currentPosition);
                setSuccess('Cargo atualizado com sucesso!');
            } else {
                await addDoc(collection(db, 'positions'), currentPosition);
                setSuccess('Cargo adicionado com sucesso!');
            }
            setCurrentPosition({ name: '' });
            setEditingPosition(null);
            fetchPositions();
        } catch (err) {
            console.error('Erro ao salvar cargo:', err);
            setError('Erro ao salvar cargo');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (position: Position) => {
        setEditingPosition(position);
        setCurrentPosition({ name: position.name });
        setError(null);
        setSuccess(null);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Tem certeza que deseja excluir este cargo?')) {
            try {
                await deleteDoc(doc(db, 'positions', id));
                setSuccess('Cargo excluído com sucesso!');
                fetchPositions();
            } catch (err) {
                console.error('Erro ao excluir cargo:', err);
                setError('Erro ao excluir cargo');
            }
        }
    };

    const handleCancelEdit = () => {
        setEditingPosition(null);
        setCurrentPosition({ name: '' });
        setError(null);
        setSuccess(null);
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Gerenciar Cargos</h2>
            </div>

            <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {editingPosition ? 'Editar Cargo' : 'Adicionar Novo Cargo'}
                    </h3>
                    {error && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-600">
                            {success}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                        <div>
                            <label htmlFor="positionName" className="block text-sm font-medium text-gray-700">
                                Nome do Cargo
                            </label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                                <input
                                    type="text"
                                    name="positionName"
                                    id="positionName"
                                    className="input-primary flex-1"
                                    value={currentPosition.name}
                                    onChange={(e) => setCurrentPosition({ name: e.target.value })}
                                    placeholder="Ex: Desenvolvedor, Designer, Gerente de Projetos"
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3">
                            {editingPosition && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="btn-secondary"
                                >
                                    Cancelar
                                </button>
                            )}
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Salvando...' : (editingPosition ? 'Atualizar' : 'Adicionar')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="mt-8 bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Cargos Cadastrados</h3>
                    {positions.length === 0 ? (
                        <p className="text-gray-500">Nenhum cargo cadastrado.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nome
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {positions.map((position) => (
                                        <tr key={position.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {position.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                                                <button
                                                    onClick={() => handleEdit(position)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Editar"
                                                >
                                                    <PencilIcon className="h-5 w-5" />
                                                </button>
                                                <button
                                                    onClick={() => position.id && handleDelete(position.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Excluir"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
} 